import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Loader2, ExternalLink } from 'lucide-react';
import { expensesAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const ExpenseRequestPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  
  const [expense, setExpense] = useState(null);
  const [upiData, setUpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Get the payment page URL (HTTPS, clickable in WhatsApp)
  const paymentPageUrl = `${window.location.origin}/pay/${id}`;

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [expenseRes, upiRes] = await Promise.all([
        expensesAPI.get(id),
        expensesAPI.getUPILink(id)
      ]);
      setExpense(expenseRes.data);
      setUpiData(upiRes.data);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load data';
      setError(typeof message === 'string' ? message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = Math.abs(amount);
    if (num === Math.floor(num)) {
      return `₹${num.toLocaleString('en-IN')}`;
    }
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleCopyLink = async () => {
    try {
      // Copy the HTTPS payment page URL (not the upi:// link)
      await navigator.clipboard.writeText(paymentPageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleWhatsAppShare = () => {
    if (!upiData?.whatsapp_message) return;
    const encodedMessage = encodeURIComponent(upiData.whatsapp_message);
    // Use location.href instead of window.open to avoid COOP issues
    window.location.href = `https://wa.me/?text=${encodedMessage}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <p className="text-lg font-medium mb-4 text-center">{error}</p>
        {error.includes('UPI ID') ? (
          <button onClick={() => navigate('/profile')} className="btn-primary">
            Add UPI ID
          </button>
        ) : (
          <button onClick={() => navigate('/home')} className="btn-secondary">
            Go Home
          </button>
        )}
      </div>
    );
  }

  const split = expense?.splits?.[0];
  const friendName = split?.user_name || split?.owed_by_phone || 'Friend';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-6 py-4 flex items-center gap-4 border-b-2 border-black">
        <button 
          onClick={() => navigate(`/expenses/${id}`)}
          className="hover:opacity-70 transition-opacity"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-heading font-bold">Request Payment</h1>
      </header>

      <main className="flex-1 p-6 pb-32 space-y-6">
        {/* Request Summary Card */}
        <div className="card-base text-center">
          <p className="text-neutral-500 mb-1">Request from</p>
          <p className="text-xl font-bold mb-4">{friendName}</p>
          <p className="text-5xl font-heading font-bold text-money-in mb-2">
            {formatCurrency(upiData?.amount || 0)}
          </p>
          <p className="text-neutral-600">
            for {expense?.description || 'Split'}
          </p>
        </div>

        {/* UPI ID Display */}
        <div>
          <p className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
            Payment will be sent to
          </p>
          <div className="card-base flex items-center justify-between">
            <div>
              <p className="font-bold font-mono">{upiData?.payee_upi_id}</p>
              <button
                onClick={() => navigate('/profile')}
                className="text-sm text-brand-purple font-medium hover:underline"
              >
                Edit in Profile
              </button>
            </div>
            <Check className="w-5 h-5 text-success" />
          </div>
        </div>

        {/* Message Preview */}
        <div>
          <p className="text-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
            Preview Message
          </p>
          <div className="card-base bg-neutral-50 font-mono text-sm whitespace-pre-wrap">
            {upiData?.whatsapp_message}
          </div>
        </div>
      </main>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black space-y-3">
        <button
          onClick={handleWhatsAppShare}
          className="btn-whatsapp"
          data-testid="share-whatsapp-button"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Share on WhatsApp
        </button>
        
        <button
          onClick={handleCopyLink}
          className="btn-secondary w-full flex items-center justify-center gap-2"
          data-testid="copy-link-button"
        >
          {copied ? (
            <>
              <Check className="w-5 h-5" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-5 h-5" />
              Copy Link
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ExpenseRequestPage;
